/**
 * File nfo-song-cycle-navigation.js
 *
 * Drives the two states of the site header:
 *
 * - the full-screen menu the toggle opens and closes
 * - the scrolled state, which fades a light background into the bar and
 *   reveals the logo once the page has scrolled past the hero lockup
 *
 * Everything visual lives in resources/sass/base/_header.scss - this file
 * only sets .is-open / .is-scrolled on the header and .nav-open on <body>.
 */
( function() {

	const header = document.getElementById( 'site-header' );

	// Return early if the header isn't on the page.
	if ( ! header ) {
		return;
	}

	const toggle  = document.getElementById( 'nav-toggle' );
	const overlay = document.getElementById( 'nav-overlay' );

	// The overlay covers these, so they are taken out of the tab order and
	// the accessibility tree while it is open. Without this, tabbing past the
	// last menu link walks through the page hidden behind it.
	const pageContent = document.querySelectorAll( '#primary, .site-footer' );

	/**
	 * The scroll position at which the bar takes on its light background.
	 *
	 * The design ties this to the hero lockup: the bar stays transparent
	 * while the large logo is still in view, then fades in as it leaves. We
	 * measure the bottom of that logo, and fall back to a fixed distance on
	 * pages that have no hero.
	 *
	 * @return {number} Distance in pixels.
	 */
	function getScrollThreshold() {
		const heroLogo = document.querySelector( '.page-hero__logo' );

		if ( ! heroLogo ) {
			return 200;
		}

		const bounds = heroLogo.getBoundingClientRect();

		return bounds.bottom + window.scrollY;
	}

	let threshold = getScrollThreshold();

	// The menu links, and the classes WordPress marks the current page with.
	// A link only counts as current when its fragment matches the address bar
	// too, which is something only the browser can tell - see
	// nfosongcycle_anchor_menu_item_classes() in inc/theme-functions.php.
	const menuLinks = header.querySelectorAll( '.nav-overlay__menu a[href]' );

	const serverCurrentClasses = [ 'current-menu-item', 'current_page_item' ];

	/**
	 * Drops a trailing slash so "/about/" and "/about" compare equal.
	 *
	 * @param {string} path A URL path.
	 * @return {string}
	 */
	function normalisePath( path ) {
		return ( '/' === path ) ? path : path.replace( /\/$/, '' );
	}

	/**
	 * Whether a menu link points at what is currently in the address bar.
	 *
	 * Reading .host, .pathname and .hash off the anchor lets the browser
	 * resolve the href for us, so relative and absolute links both work.
	 *
	 * @param {HTMLAnchorElement} link A menu link.
	 * @return {boolean}
	 */
	function isCurrentLink( link ) {
		return link.host === window.location.host
			&& normalisePath( link.pathname ) === normalisePath( window.location.pathname )
			&& link.hash === window.location.hash;
	}

	/**
	 * Marks the one menu item matching the current URL, fragment included.
	 *
	 * With no fragment in the address bar that is the page link itself; with
	 * one it is the anchor item, and the page link - which WordPress marked
	 * server side, where the fragment is not visible - gives up the state.
	 *
	 * @return void
	 */
	function updateCurrentLink() {
		menuLinks.forEach( function( link ) {
			const item    = link.closest( 'li' ) || link;
			const current = isCurrentLink( link );

			item.classList.toggle( 'is-current', current );

			if ( current ) {
				link.setAttribute( 'aria-current', link.hash ? 'location' : 'page' );
			} else {
				link.removeAttribute( 'aria-current' );

				serverCurrentClasses.forEach( function( className ) {
					item.classList.remove( className );
				} );
			}
		} );
	}

	/**
	 * Adds or removes the scrolled state on the header.
	 *
	 * @return void
	 */
	function updateScrollState() {
		header.classList.toggle( 'is-scrolled', window.scrollY >= threshold );
	}

	/**
	 * Opens or closes the full-screen menu.
	 *
	 * @param {boolean} open Whether the menu should be open.
	 * @return void
	 */
	function setMenuState( open ) {
		header.classList.toggle( 'is-open', open );
		document.body.classList.toggle( 'nav-open', open );
		toggle.setAttribute( 'aria-expanded', open ? 'true' : 'false' );

		// Keep whichever of the two is hidden out of the tab order and the
		// accessibility tree.
		if ( open ) {
			overlay.removeAttribute( 'inert' );
			pageContent.forEach( function( element ) {
				element.setAttribute( 'inert', '' );
			} );
		} else {
			overlay.setAttribute( 'inert', '' );
			pageContent.forEach( function( element ) {
				element.removeAttribute( 'inert' );
			} );
		}
	}

	/**
	 * Whether the menu is currently open.
	 *
	 * @return {boolean}
	 */
	function isOpen() {
		return header.classList.contains( 'is-open' );
	}

	// The toggle and overlay are both required for the menu; the scrolled
	// state works without them.
	if ( toggle && overlay ) {

		setMenuState( false );

		toggle.addEventListener( 'click', function() {
			const open = ! isOpen();

			setMenuState( open );

			// Move the keyboard into the menu when it opens, so the next Tab
			// walks the links rather than starting from the toggle again.
			if ( open ) {
				const firstLink = overlay.querySelector( 'a' );

				if ( firstLink ) {
					firstLink.focus();
				}
			}
		} );

		// Following a link closes the menu, which matters for same-page
		// anchors where no navigation happens.
		overlay.addEventListener( 'click', function( event ) {
			if ( event.target.closest( 'a' ) ) {
				setMenuState( false );
			}
		} );

		// Escape closes the menu and returns focus to the toggle.
		document.addEventListener( 'keydown', function( event ) {
			if ( 'Escape' === event.key && isOpen() ) {
				setMenuState( false );
				toggle.focus();
			}
		} );

	}

	// Passive: this listener only reads scroll position, it never blocks it.
	window.addEventListener( 'scroll', updateScrollState, { passive: true } );

	// The hero is fluid, so its height - and with it the threshold - changes
	// with the viewport.
	window.addEventListener( 'resize', function() {
		threshold = getScrollThreshold();
		updateScrollState();
	} );

	// The hero artwork loads after parse, and the logo moves as it does.
	window.addEventListener( 'load', function() {
		threshold = getScrollThreshold();
		updateScrollState();
	} );

	// Following a menu anchor only changes the fragment, so hashchange is
	// what tells us the current item has moved.
	window.addEventListener( 'hashchange', updateCurrentLink );

	updateScrollState();
	updateCurrentLink();

}() );
